import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosBitrixInstance, axiosInstance } from "../../../resources/requestInfo";
import { getPartnerIdFromStorage } from "../../../utils/storage/partnerId";

import { IItem } from "./types";
import { historyRoutes } from "./routes";

interface IActiveCourt {
  courtId: string;
  courtName: string;
  courtType: string;
  [key: string]: any;
}

interface IPaymentDate {
  startTime: string;
  courtId: string;
  productId: string;
  paidAt: string;
}

// Ключ сопоставления позиции 1С с нашим заказом: слот + что именно куплено.
const slotKey = (startTime?: string, courtId?: string, productId?: string) =>
  `${startTime || ''}|${courtId || ''}|${productId || ''}`;

export const fetchHistory = createAsyncThunk<IItem[]>(
  "history/fetchHistory",
  async (_, { rejectWithValue }) => {
    try {
      const partnerId = getPartnerIdFromStorage();

      if (!partnerId) {
        return rejectWithValue('PartnerId отсутствует. Необходима авторизация для просмотра истории.');
      }

      const historyResponse = await axiosBitrixInstance.get(
        historyRoutes.GET_PURCHASE_HISTORY(partnerId)
      );

      const ph = historyResponse?.data?.purchaseHistory;

      // 1С отдаёт объект { rent, products, storage }, а не массив.
      // Сплющиваем в единый список — как это делает мобильное приложение.
      let purchaseHistory: any[];
      if (Array.isArray(ph)) {
        purchaseHistory = ph;
      } else if (ph && typeof ph === 'object') {
        purchaseHistory = [
          ...(ph.rent || []).map((el: any) => ({ ...el, itemType: 'rent' })),
          ...(ph.products || []).map((el: any) => ({ ...el, itemType: 'product' })),
          ...(ph.storage || []).map((el: any) => ({ ...el, itemType: 'storage' })),
        ];
      } else {
        return rejectWithValue('Некорректный формат данных истории покупок');
      }

      // Даты оплаты живут только в нашей БД — в ответе 1С их нет.
      // Best-effort: не доехали, история всё равно отрисуется, просто без даты.
      let paidAtMap = new Map<string, string>();
      try {
        const paymentDatesResponse = await axiosInstance.get<IPaymentDate[]>(
          historyRoutes.GET_PAYMENT_DATES
        );
        const paymentDates = paymentDatesResponse?.data || [];
        paidAtMap = new Map(
          paymentDates.map((p) => [slotKey(p.startTime, p.courtId, p.productId), p.paidAt])
        );
      } catch (paymentDatesError) {
        console.warn('Ошибка получения дат оплаты:', paymentDatesError);
      }

      let courtMap = new Map<string, IActiveCourt>();
      try {
        const activeCourtsResponse = await axiosBitrixInstance.get(historyRoutes.GET_ACTIVE_COURTS);
        const activeCourts: IActiveCourt[] = activeCourtsResponse?.data?.activeCourts || [];
        courtMap = new Map(activeCourts.map((court) => [court.courtId, court]));
      } catch (courtError) {
        console.warn('Ошибка получения информации о кортах, возвращаем историю без названий кортов:', courtError);
      }

      const enrichedHistory = purchaseHistory.map((item: any) => {
        const court = courtMap.get(item.courtId);

        let duration = 0;
        let dateForSort = item.startTime || '';
        let timeStart = '';

        if (item.startTime && item.finishTime) {
          const start = new Date(item.startTime);
          const finish = new Date(item.finishTime);
          duration = Math.round((finish.getTime() - start.getTime()) / (1000 * 60)); // в минутах
          dateForSort = item.startTime;
          timeStart = item.startTime;
        }

        const type = item.productId ? 'Ракетка' : 'Корт';
        const payment = item.productId ? (item.productSum || 0) : (item.rentSum || 0);
        const amount = item.productId ? String(item.productQuantity || 0) : '';

        return {
          ...item,
          courtName: court?.courtName || '',
          courtType: court?.courtType || '',
          racket: !!item.productId,
          amount,
          duration,
          payment,
          dateForSort,
          timeStart,
          type,
          paidAt: paidAtMap.get(slotKey(item.startTime, item.courtId, item.productId)) || null,
        };
      });

      return enrichedHistory;
    } catch (error: any) {
      console.log("error fetchHistory >>>", error);
      return rejectWithValue(error.response?.data?.message || 'Ошибка получения истории покупок');
    }
  }
);
